import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import useSubscription from '@src/hooks/useSubscription';
import VerticalLine from '@src/sld/components/Line/VerticalLine';
import MultiSwitchConnector from '@src/sld/components/MultiSwitchConnector/MultiSwitchConnector';
import Resistor from '@src/sld/components/Resistor/Resistor';
import { DataPointWithValue } from '@src/types/DataPoint';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import storeConnector from '@store/storeConnector';
import { ReactElement } from 'react';
import BreakerBasic from '../../components/Breaker/BreakerBasic';
import BreakerStack from './components/BreakerStack';
import Header from './components/Header';
import { Hanford_2_Device_Points_Config } from './devicePointsConfig';

function Hanford_2_Diagram(): ReactElement {
  type EngineeringShapeConfig = {
    shape: string;
    internalId: string;
    dataSourceDeviceType: string;
    label: string;
    dataSourceDeviceEngineeringId: string;
    points: {
      label: string;
      pointName: string;
      isStatusBinaryPoint: boolean;
    }[];
    dataPoints: DataPointWithValue[] | [];
  };

  const CB_152_Breaker_Config: EngineeringShapeConfig | undefined =
    Hanford_2_Device_Points_Config.CB_152_BREAKER_STATUS;
  const M2_Breaker_Config: EngineeringShapeConfig | undefined =
    Hanford_2_Device_Points_Config.M2_BREAKER_STATUS;
  const M2_Breaker_Points_Config: EngineeringShapeConfig | undefined =
    Hanford_2_Device_Points_Config.M2_BREAKER_POINTS;
  const F2_1_Breaker_Config: EngineeringShapeConfig | undefined =
    Hanford_2_Device_Points_Config.F2_1_BREAKER_STATUS;
  const F2_1_Breaker_Points_Config: EngineeringShapeConfig | undefined =
    Hanford_2_Device_Points_Config.F2_1_BREAKER_POINTS;
  const A2_Breaker_Config: EngineeringShapeConfig | undefined =
    Hanford_2_Device_Points_Config.A2_BREAKER_STATUS;
  const A2_Breaker_Points_Config: EngineeringShapeConfig | undefined =
    Hanford_2_Device_Points_Config.A2_BREAKER_POINTS;

  const { data: rtacWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'hanford_2_sld_rtac',
      {},
      { enabled: true },
    );

  const sldDataPointsWithValues = useSubscription(
    rtacWidgetConfig?.dataPoints || [],
  );

  const getDataPointsForThisDevice = (
    dataSourceDeviceEngineeringId: string,
  ): DataPointWithValue[] => {
    return sldDataPointsWithValues.filter(
      (point) => point.sourceDeviceId === dataSourceDeviceEngineeringId,
    );
  };

  const populateConfigDataPoints = (config: EngineeringShapeConfig) => {
    if (!config?.points || sldDataPointsWithValues.length === 0) {
      config.dataPoints = [];
      return;
    }

    const deviceDataPoints = getDataPointsForThisDevice(
      config.dataSourceDeviceEngineeringId,
    );

    const dataPointMap = new Map(
      deviceDataPoints.map((dp) => [dp.pointName.toLowerCase(), dp]),
    );

    config.dataPoints = config.points
      .map((configPoint) => {
        const matchingDataPoint = dataPointMap.get(
          configPoint.pointName.toLowerCase(),
        );

        return matchingDataPoint
          ? {
              ...matchingDataPoint,
              displayName: configPoint.label,
            }
          : null;
      })
      .filter(
        (dataPoint): dataPoint is DataPointWithValue => dataPoint !== null,
      );
  };

  populateConfigDataPoints(CB_152_Breaker_Config);
  populateConfigDataPoints(M2_Breaker_Points_Config);
  populateConfigDataPoints(F2_1_Breaker_Config);
  populateConfigDataPoints(F2_1_Breaker_Points_Config);
  populateConfigDataPoints(A2_Breaker_Config);
  populateConfigDataPoints(A2_Breaker_Points_Config);

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='200 40 1200 700'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor', overflowX: 'auto' }}
    >
      <title>Hanford 2 SLD</title>
      <Header />

      {/* top breaker */}
      <g transform='translate(728 146)'>
        <BreakerBasic
          labels={[{ label: 'CB 152-6', xCoordinate: 40, yCoordinate: 15 }]}
          translateXCoordinate={0}
          translateYCoordinate={0}
          dataPointsWithValues={[]}
          defaultHot={true}
        />
      </g>

      {/* vertical line below top breaker */}
      <g transform='translate(742 163)'>
        <VerticalLine
          translateXCoordinate={0}
          translateYCoordinate={11}
          length={40}
          color='white'
          width={0.5}
        />
      </g>

      {/* resistor */}
      <g transform='rotate(90 700 783) translate(127 -35)'>
        <Resistor label={''} />
      </g>

      {/* resistor text */}
      <g transform='translate(770 230)'>
        <text
          x={0}
          y={0}
          fontSize={8}
          fill='white'
          stroke='white'
          strokeWidth={0.2}
        >
          HANBESSMPT
        </text>
      </g>

      {/* vertical line above connector */}
      <g transform='translate(778 255)'>
        <VerticalLine
          translateXCoordinate={-35}
          translateYCoordinate={-17}
          length={62}
          color='white'
          width={0.5}
        />
      </g>

      {/* connector above breaker stacks */}
      <g transform='translate(500 -200)'>
        <MultiSwitchConnector
          translateXCoordinate={80}
          translateYCoordinate={-45}
          horizontalLength={300}
          verticalLength={20}
          startingXCoordinate={0}
        />
      </g>

      {/* breaker stack 1 */}
      <g transform='translate(693 -403)'>
        <BreakerStack
          breakerConnected={
            A2_Breaker_Config.dataPoints.find((point) => {
              return (
                point.pointName ===
                A2_Breaker_Config.points.find(
                  (point) => point.isStatusBinaryPoint,
                )?.pointName
              );
            })?.value === 1 || false
          }
          deviceDataPoints={A2_Breaker_Points_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              A2_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-A2'}
          sectionLabel={'52-A2'}
          deviceDataPointsSectionXCoordinate={-340}
          deviceDataPointsSectionYCoordinate={750}
          verticalLineLength={50}
          displayArrowDown={true}
          displayArrowDownYCoordinate={795}
        />

        <text x={-140} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          AUX FEED #2
        </text>
      </g>

      {/* breaker stack 2 */}
      <g transform='translate(993 -403)'>
        <BreakerStack
          breakerConnected={
            M2_Breaker_Config.dataPoints.find(
              (point) =>
                point.pointName ===
                M2_Breaker_Config.points.find(
                  (point) => point.isStatusBinaryPoint,
                )?.pointName,
            )?.value == 1 || false
          }
          deviceDataPoints={M2_Breaker_Points_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              M2_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-M2'}
          sectionLabel={'52-M2'}
          deviceDataPointsSectionXCoordinate={-30}
          deviceDataPointsSectionYCoordinate={750}
          displayArrowDown={false}
          verticalLineLength={100}
        />
      </g>

      {/* breaker stack 4 */}
      <g transform='translate(993 -282)'>
        <BreakerStack
          breakerConnected={
            F2_1_Breaker_Config.dataPoints.find(
              (point) =>
                point.pointName ===
                F2_1_Breaker_Config.points.find(
                  (point) => point.isStatusBinaryPoint,
                )?.pointName,
            )?.value == 1 || false
          }
          deviceDataPoints={F2_1_Breaker_Points_Config.dataPoints.filter(
            (point) =>
              point.pointName !==
              F2_1_Breaker_Config.points.find((p) => p.isStatusBinaryPoint)
                ?.pointName,
          )}
          translateXCoordinate={0}
          translateYCoordinate={0}
          feederALabel={''}
          breakerLabel={'52-F2-1'}
          sectionLabel={'52-F2-1'}
          deviceDataPointsSectionXCoordinate={-25}
          deviceDataPointsSectionYCoordinate={795}
          displayArrowDown={true}
          displayArrowDownYCoordinate={795}
          verticalLineLength={50}
        />

        <text x={-140} y={820} fill='currentColor' stroke='none' fontSize='8px'>
          BESS CIRCUIT
        </text>

        <text x={-125} y={830} fill='currentColor' stroke='none' fontSize='8px'>
          NO. 2-1
        </text>

        <text x={-147} y={840} fill='currentColor' stroke='none' fontSize='8px'>
          12 PCS MODULES
        </text>
      </g>
    </svg>
  );
}

export default storeConnector(Hanford_2_Diagram, {
  config: ['siteMeta'],
});
